import {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  InputHTMLAttributes,
  Ref,
  useImperativeHandle,
  useRef,
} from "react";
import {
  borderClassName,
  inputClassName,
  inputColorClassName,
  roundedClassName,
  sizeClassName,
} from "./commonClassNames";
import { BorderProps, InputColorProps, InputTextSizeProp } from "./commonProps";

type _CustomRefType = {
  focus: () => void;
};

export type InputDateProps = Omit<InputHTMLAttributes<HTMLInputElement>, "className" | "onChange" | "type"> & {
  ref?: Ref<_CustomRefType>;
} & BorderProps &
  InputColorProps & {
    setValue?: (value: string) => void;
    openPickerOnFocus?: boolean;
    className?: string;
    small?: boolean;
  };

export const InputDate = forwardRef<_CustomRefType, InputDateProps>(
  (
    {
      transparent,
      neutral,
      setValue,
      border,
      grouped,
      min,
      max,
      openPickerOnFocus,
      onBlur,
      className,
      small,
      ...props
    },
    ref,
  ) => {
    const combinedClassName = [
      "font-normal",
      small === true ? "h-8" : "",
      inputClassName(),
      borderClassName({ border, grouped }),
      roundedClassName({ grouped }),
      sizeClassName({ size: small ? "small" : undefined }),
      inputColorClassName({ transparent, neutral }),
      className ?? "",
    ].join(" ");

    const inputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        focus() {
          if (!inputRef.current) {
            return;
          }

          const current = inputRef.current;
          current.focus();

          if (openPickerOnFocus) {
            current.showPicker();
          }
        },
      }),
      [openPickerOnFocus],
    );

    const handleOnBlur: FocusEventHandler<HTMLInputElement> = (event) => {
      const value = event.target.value;

      // Apply constraints
      if (typeof min === "string" && value < min) {
        setValue?.(min);
      }
      if (typeof max === "string" && value > max) {
        setValue?.(max);
      }

      onBlur?.(event);
    };

    const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const value = event.target.value;
      setValue?.(value);
    };

    return (
      <input
        type="date"
        className={combinedClassName}
        min={min}
        max={max}
        onBlur={handleOnBlur}
        onChange={onChange}
        {...props}
        ref={inputRef}
      />
    );
  },
);
